import { supabase } from "../../../config/supabaseClient";
import type { ThemeTokens } from "./types";

/** Catalog id for Add on Products and Link in Bio. */
export const STORE_PRODUCT_CATALOG_TYPE = "store-products";

/** `stayuptodate_components` / `template_components` type. */
export const STORE_PRODUCT_DB_TYPE = "store-product";

/** Renderer switch in the classic link-in-bio stack. */
export const STORE_PRODUCT_DISPLAY_NAME = "My Store Product";

export type StoreProductDisplay = "link" | "full_view";

/** Page-section layout. The product record still owns its own link / full view. */
export type DigitalProductLayout = "row" | "featured" | "stack";
export type DigitalProductAlign = "left" | "center" | "right";

export function digitalProductLayout(style: unknown): DigitalProductLayout {
  const value = String(style || "").trim();
  if (value === "featured" || value === "stack") return value;
  return "row";
}

/** Stack card position. Row and featured always fill the section. */
export function digitalProductAlign(value: unknown): DigitalProductAlign {
  const align = String(value || "").trim();
  if (align === "left" || align === "right") return align;
  return "center";
}

export type DigitalProductSummary = {
  id: string;
  kind: "digital_product" | "freebie";
  title: string;
  subtitle: string;
  buttonText: string;
  thumbnailUrl: string;
  displayType: StoreProductDisplay;
  price: number;
  status: string;
};

export type WebsiteStoreDesignTokens = {
  templateName: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  forthColor: string;
  primaryFontFamily: string;
  secondaryFontFamily: string;
  fontFamily: string;
  buttonTextColor: string;
};

const PRODUCT_COLUMNS =
  "id, name, price, status, product_image, metadata, is_product_delete";

export function mapStoreProductRow(row: any): DigitalProductSummary | null {
  const kind = row?.name === "digital_product" ? "digital_product" : row?.name === "freebie" ? "freebie" : null;
  if (!kind || !row?.id) return null;
  const details = row?.metadata?.product_details || {};
  const price = Number(row?.price) || 0;
  return {
    id: String(row.id),
    kind,
    title: String(details.title || "Untitled product").trim() || "Untitled product",
    subtitle: String(details.subtitle || "").trim(),
    buttonText:
      String(details.buttonText || "").trim() ||
      (kind === "digital_product" ? "Purchase" : "Get it"),
    thumbnailUrl: String(
      details.thumbnailUrl || row?.product_image || "",
    ).trim(),
    displayType: details.displayType === "full_view" ? "full_view" : "link",
    price,
    status: String(row?.status || ""),
  };
}

export function formatStoreProductPrice(product: DigitalProductSummary): string {
  if (product.kind === "freebie" || product.price <= 0) return "Free";
  return `$${product.price.toFixed(2)}`;
}

export async function fetchPublishedStoreProducts(
  userId: string,
): Promise<DigitalProductSummary[]> {
  const { data, error } = await supabase
    .from("all_products")
    .select(PRODUCT_COLUMNS)
    .eq("user_id", userId)
    .neq("is_product_delete", true)
    .eq("status", "active")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || [])
    .map(mapStoreProductRow)
    .filter((row): row is DigitalProductSummary => Boolean(row));
}

export async function fetchStoreProductById(
  productId: string,
): Promise<DigitalProductSummary | null> {
  const id = String(productId || "").trim();
  if (!id) return null;
  const { data, error } = await supabase
    .from("all_products")
    .select(PRODUCT_COLUMNS)
    .eq("id", id)
    .neq("is_product_delete", true)
    .maybeSingle();
  if (error) throw error;
  return data ? mapStoreProductRow(data) : null;
}

/**
 * Survive publish remounts: dashboard/canvas often remount the section twice
 * while the first fetch is still in flight. Share one promise + warm cache so
 * the card never drops back to a skeleton between those remounts.
 */
const storeProductWarmCache = new Map<string, DigitalProductSummary>();
const storeProductInflight = new Map<
  string,
  Promise<DigitalProductSummary | null>
>();

export function getCachedStoreProduct(
  productId: string,
): DigitalProductSummary | null {
  const id = String(productId || "").trim();
  return id ? storeProductWarmCache.get(id) || null : null;
}

export function primeStoreProductCache(
  product: DigitalProductSummary | null | undefined,
): void {
  const id = String(product?.id || "").trim();
  if (!id || !product) return;
  storeProductWarmCache.set(id, product);
}

export function fetchStoreProductCached(
  productId: string,
): Promise<DigitalProductSummary | null> {
  const id = String(productId || "").trim();
  if (!id) return Promise.resolve(null);
  const cached = storeProductWarmCache.get(id);
  if (cached) return Promise.resolve(cached);
  const pending = storeProductInflight.get(id);
  if (pending) return pending;
  const request = fetchStoreProductById(id)
    .then((row) => {
      if (row) storeProductWarmCache.set(id, row);
      return row;
    })
    .finally(() => {
      storeProductInflight.delete(id);
    });
  storeProductInflight.set(id, request);
  return request;
}

/** Website theme → the token shape the store card and checkout already use. */
export function storeDesignTokensFromTheme(
  T: Pick<
    ThemeTokens,
    | "bg"
    | "ink"
    | "muted"
    | "accent"
    | "accentInk"
    | "display"
    | "body"
  >,
): WebsiteStoreDesignTokens {
  return {
    templateName: "website",
    primaryColor: T.bg,
    secondaryColor: T.ink,
    tertiaryColor: T.muted,
    forthColor: T.accent,
    primaryFontFamily: T.display,
    secondaryFontFamily: T.body,
    fontFamily: T.body,
    buttonTextColor: T.accentInk,
  };
}

/** Read theme variables inherited from the website canvas. */
export function readWebsiteStoreTokens(
  from: HTMLElement | null,
): WebsiteStoreDesignTokens | null {
  let node: HTMLElement | null = from;
  while (node) {
    const cs = window.getComputedStyle(node);
    const ink = cs.getPropertyValue("--tpl-ink").trim();
    const accent = cs.getPropertyValue("--tpl-accent").trim();
    if (ink && accent) {
      return {
        templateName: "website",
        primaryColor: cs.getPropertyValue("--tpl-bg").trim() || "#ffffff",
        secondaryColor: ink,
        tertiaryColor: cs.getPropertyValue("--tpl-muted").trim() || ink,
        forthColor: accent,
        primaryFontFamily: cs.getPropertyValue("--tpl-display").trim(),
        secondaryFontFamily: cs.getPropertyValue("--tpl-body").trim(),
        fontFamily: cs.getPropertyValue("--tpl-body").trim(),
        buttonTextColor:
          cs.getPropertyValue("--tpl-accent-ink").trim() || "#ffffff",
      };
    }
    node = node.parentElement;
  }
  return null;
}
